// Server lists the sections show (letters, flags, proposals, recordings, the shared session):
// fetched when a screen opens and after every successful sync, cached in IndexedDB
// (kv "remote:<key>") so the last copy shows offline. Writes do not go through here: they are
// queued with queueWrite() and applied optimistically to the cached copy with mutateRemote().
import { useCallback, useEffect, useEffectEvent, useRef, useSyncExternalStore } from 'react';
import { ApiError } from './api';
import { getDB, kvGet, kvSet } from './db';
import { store } from '../state/store';

export type RemoteStatus = 'idle' | 'loading' | 'ok' | 'offline' | 'error' | 'auth' | 'demo';

export interface RemoteEntry<T> {
  data: T | undefined;
  status: RemoteStatus;
  /** When `data` was fetched (ISO); undefined for an optimistic-only copy. */
  at?: string;
  error?: string;
}

const entries = new Map<string, RemoteEntry<unknown>>();
const listeners = new Map<string, Set<() => void>>();
const inflight = new Map<string, Promise<void>>();
const EMPTY: RemoteEntry<never> = { data: undefined, status: 'idle' };

/**
 * Row validation per key (lib/rows.ts): applied to the network answer AND to the IndexedDB copy,
 * so a malformed cached row can never keep a screen broken. Registered by the first useRemote /
 * loadRemote call that passes one.
 */
export type Normalize<T> = (data: unknown) => T | undefined;
const normalizers = new Map<string, Normalize<unknown>>();
function norm<T>(key: string, data: unknown): T | undefined {
  const f = normalizers.get(key);
  if (!f) return data as T;
  try {
    return f(data) as T | undefined;
  } catch {
    return undefined;
  }
}

/**
 * Writes still in the outbox, laid over a fresh server answer per key: a change made before the
 * screen's first load (or while a load was out) is not on the server yet, and the server's list
 * must not hide it until the next sync. Registered by the module that queues those writes.
 */
export type PendingOverlay<T> = (data: T, key: string) => Promise<T>;
const overlays = new Map<string, PendingOverlay<unknown>>();
export function registerPendingOverlay<T>(key: string, f: PendingOverlay<T>): void {
  overlays.set(key, f as PendingOverlay<unknown>);
}
// Overlays for every key that starts with a prefix (the recording lists "recordings:<…>", whose
// keys are per target; trim and delete of own recordings, recordings/edits.ts; takes still queued,
// recordings/outbox.ts). Several may share a prefix; they run in the order registered.
const prefixOverlays: { prefix: string; f: PendingOverlay<unknown> }[] = [];
export function registerPendingOverlayPrefix<T>(prefix: string, f: PendingOverlay<T>): void {
  prefixOverlays.push({ prefix, f: f as PendingOverlay<unknown> });
}
/** The keys of the lists in memory that start with `prefix` (to patch every list showing a row). */
export function remoteKeys(prefix: string): string[] {
  return [...entries.keys()].filter((k) => k.startsWith(prefix));
}
async function withPending<T>(key: string, data: T): Promise<T> {
  for (const { prefix, f } of prefixOverlays) {
    if (!key.startsWith(prefix)) continue;
    try {
      data = (await f(data, key)) as T;
    } catch {
      /* the outbox cannot be read */
    }
  }
  const f = overlays.get(key);
  if (!f) return data;
  try {
    return (await f(data, key)) as T;
  } catch {
    return data; // the outbox cannot be read: the server's list as it is
  }
}

function emit(key: string) {
  for (const l of listeners.get(key) ?? []) l();
}
function put<T>(key: string, e: RemoteEntry<T>) {
  entries.set(key, e as RemoteEntry<unknown>);
  emit(key);
}

export function getRemote<T>(key: string): RemoteEntry<T> {
  return (entries.get(key) as RemoteEntry<T> | undefined) ?? EMPTY;
}

/** Fetch now (single-flight per key). Keeps the previous data on failure. */
export function loadRemote<T>(key: string, fetcher: () => Promise<T>, normalize?: Normalize<T>): Promise<void> {
  if (normalize) normalizers.set(key, normalize as Normalize<unknown>);
  const running = inflight.get(key);
  if (running) return running;
  const p = (async () => {
    const prev = getRemote<T>(key);
    if (prev.data === undefined) {
      // First load in this page: show the cached copy while the network answers.
      try {
        const cached = (await kvGet(await getDB(), `remote:${key}`)) as { data: T; at: string } | undefined;
        const copy = cached ? norm<T>(key, cached.data) : undefined;
        // the writes still waiting on this device are laid over the cached copy too: offline, it is
        // all this page will see (overlays apply the outbox to a list, so applying twice is harmless)
        const data = copy !== undefined ? await withPending(key, copy) : undefined;
        if (cached && data !== undefined && getRemote<T>(key).data === undefined) put(key, { data, at: cached.at, status: 'loading' });
      } catch {
        /* no IndexedDB (tests) */
      }
    }
    if (store.get().identity === 'demo') {
      put(key, { ...getRemote<T>(key), status: 'demo' });
      return;
    }
    put(key, { ...getRemote<T>(key), status: 'loading' });
    try {
      const fresh = norm<T>(key, await fetcher());
      if (fresh === undefined) throw new ApiError(502, 'bad-response', 'unusable list');
      // the same rows as before (a reload after a sync that changed nothing): the same object, so
      // the screens reading it keep their render (docs/audit/performance.md "Pages")
      const data = sameData(getRemote<T>(key).data, await withPending(key, fresh));
      const at = new Date().toISOString();
      put(key, { data, at, status: 'ok' });
      try {
        await kvSet(await getDB(), `remote:${key}`, { data, at });
      } catch {
        /* ignore */
      }
    } catch (err) {
      const e = err instanceof ApiError ? err : new ApiError(0, 'unknown', String(err));
      put(key, { ...getRemote<T>(key), status: e.auth ? 'auth' : e.status === 0 ? 'offline' : 'error', error: e.message });
    }
  })().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

function sameData<T>(prev: T | undefined, next: T): T {
  if (prev === undefined || prev === next) return next;
  try {
    return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
  } catch {
    return next;
  }
}

/** Optimistic local change to a cached list (also written to IndexedDB). */
export function mutateRemote<T>(key: string, fn: (data: T | undefined) => T): void {
  const prev = getRemote<T>(key);
  const data = fn(prev.data);
  put(key, { ...prev, data });
  void (async () => {
    try {
      await kvSet(await getDB(), `remote:${key}`, { data, at: prev.at ?? new Date(0).toISOString() });
    } catch {
      /* ignore */
    }
  })();
}

/**
 * Subscribe to a server list. Loads on mount and again after each successful sync (so queued
 * writes that just reached the server show their server-side result).
 */
export function useRemote<T>(key: string, fetcher: () => Promise<T>, normalize?: Normalize<T>): RemoteEntry<T> & { reload: () => Promise<void> } {
  if (normalize && !normalizers.has(key)) normalizers.set(key, normalize as Normalize<unknown>);
  const entry = useSyncExternalStore(
    (l) => {
      let set = listeners.get(key);
      if (!set) listeners.set(key, (set = new Set()));
      set.add(l);
      return () => set!.delete(l);
    },
    () => getRemote<T>(key),
    () => getRemote<T>(key),
  );
  // The fetcher is a stable call into the api client per key; do not refetch on identity change
  // (callers pass a new closure each render): the latest one is read when a load starts.
  const reload = useReloadOnSync(key, fetcher, normalize);
  return { ...entry, reload };
}

/** Loads `key` on mount and again after each successful sync (useRemote, useRemoteData). */
function useReloadOnSync<T>(key: string, fetcher: () => Promise<T>, normalize?: Normalize<T>): () => Promise<void> {
  const latest = useRef({ fetcher, normalize });
  useEffect(() => {
    latest.current = { fetcher, normalize };
  });
  const reload = useCallback(() => loadRemote(key, latest.current.fetcher, latest.current.normalize), [key]);
  useEffect(() => {
    void reload();
    let last = store.get().sync.lastSync;
    let identity = store.get().identity;
    return store.subscribe(() => {
      const s = store.get();
      if (s.sync.lastSync !== last || s.identity !== identity) {
        last = s.sync.lastSync;
        identity = s.identity;
        if (s.sync.state === 'idle') void reload();
      }
    });
  }, [reload]);
  return reload;
}

/**
 * useRemote for a reader that needs only the rows (not the status): it re-renders only when the rows
 * change, not when a reload starts and ends. For widgets repeated down a page (FlagButton: one per
 * example, each re-rendered twice by every sync while the page scrolled).
 */
export function useRemoteData<T>(key: string, fetcher: () => Promise<T>, normalize?: Normalize<T>): T | undefined {
  if (normalize && !normalizers.has(key)) normalizers.set(key, normalize as Normalize<unknown>);
  const subscribe = useCallback(
    (l: () => void) => {
      let set = listeners.get(key);
      if (!set) listeners.set(key, (set = new Set()));
      set.add(l);
      return () => set!.delete(l);
    },
    [key],
  );
  const data = useSyncExternalStore(
    subscribe,
    () => getRemote<T>(key).data,
    () => getRemote<T>(key).data,
  );
  useReloadOnSync(key, fetcher, normalize);
  return data;
}

const warmed = new Set<string>();

/**
 * Read a server list as cached (no reload after every sync): for widgets that only need to know
 * what is there, e.g. FlagButton showing the flags on its target. The first use in a page loads
 * it once (the IndexedDB copy first, then the network).
 */
export function useRemoteCache<T>(key: string, fetcher: () => Promise<T>, normalize?: Normalize<T>): RemoteEntry<T> {
  if (normalize && !normalizers.has(key)) normalizers.set(key, normalize as Normalize<unknown>);
  const entry = useSyncExternalStore(
    (l) => {
      let set = listeners.get(key);
      if (!set) listeners.set(key, (set = new Set()));
      set.add(l);
      return () => set!.delete(l);
    },
    () => getRemote<T>(key),
    () => getRemote<T>(key),
  );
  // The fetcher is a stable call into the api client per key: loading again is a matter of the key.
  const warm = useEffectEvent(() => {
    if (getRemote<T>(key).data === undefined) void loadRemote(key, fetcher, normalize);
  });
  useEffect(() => {
    if (warmed.has(key)) return;
    warmed.add(key);
    warm();
  }, [key]);
  return entry;
}

/**
 * "Tyhjennä välimuisti" (the error screen): forget every cached server list on this device, in
 * memory and in IndexedDB. Screens fetch them again when opened. Outboxes are not touched.
 */
export async function clearRemoteCache(): Promise<void> {
  try {
    const db = await getDB();
    const keys = await db.getAllKeys('kv');
    await Promise.all(keys.filter((k) => String(k).startsWith('remote:')).map((k) => db.delete('kv', k)));
  } catch {
    /* no IndexedDB: memory only */
  }
  const keys = [...entries.keys()];
  entries.clear();
  inflight.clear();
  warmed.clear();
  for (const k of keys) emit(k);
}

/** Tests only. */
export function resetRemote(): void {
  warmed.clear();
  entries.clear();
  inflight.clear();
}
